/**
 * The small controls the Studio's design deck is built from.
 *
 * The deck puts columns, filters, totals and style on one screen, so every control in it is
 * denser than a form field and none may open a second screen: what needs more room than the
 * deck has opens as a popover over the report, and closes the moment it is not wanted.
 */
import { useEffect, type ReactNode } from 'react'
import clsx from 'clsx'
import { X } from 'lucide-react'

/** A deck panel: a title row over its controls. */
export function DeckPanel({
  id,
  title,
  icon,
  meta,
  active,
  children,
}: {
  id: string
  title: string
  icon: ReactNode
  /** Right-aligned context beside the title — a count, a quick action. */
  meta?: ReactNode
  /** Below `lg` the deck shows one panel at a time; this one is the one. */
  active: boolean
  children: ReactNode
}) {
  return (
    <section
      aria-label={title}
      data-testid={`deck-panel-${id}`}
      className={clsx(
        'min-w-0 flex-col gap-2 border-white/[0.05] p-3 lg:flex lg:border-r lg:last:border-r-0',
        active ? 'flex' : 'hidden',
      )}
    >
      <div className="flex items-center gap-1.5">
        <span className="text-amber-200">{icon}</span>
        <h3 className="text-[9px] font-semibold uppercase tracking-[0.18em] text-amber-200/85">{title}</h3>
        <span className="ml-auto flex items-center gap-2 text-[10px] text-stone-500">{meta}</span>
      </div>
      {children}
    </section>
  )
}

/** On/off, one line tall. The label is the control's accessible name. */
export function Switch({
  checked,
  onChange,
  label,
  title,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  title?: string
}) {
  return (
    <label
      title={title}
      className="flex cursor-pointer items-center justify-between gap-2 text-[11px] text-stone-300"
    >
      <span>{label}</span>
      <span className="relative inline-flex h-4 w-7 shrink-0 items-center">
        <input
          type="checkbox"
          role="switch"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          className="peer sr-only"
        />
        <span className="absolute inset-0 rounded-full bg-stone-700 transition peer-checked:bg-amber-300/80 peer-focus-visible:ring-2 peer-focus-visible:ring-amber-300/50" />
        <span className="absolute left-0.5 h-3 w-3 rounded-full bg-stone-300 transition peer-checked:translate-x-3 peer-checked:bg-black" />
      </span>
    </label>
  )
}

/** A floating panel under its trigger. The caller holds the `relative` wrapper and the dismiss ref. */
export function PopoverPanel({
  label,
  align = 'left',
  className,
  children,
}: {
  label: string
  align?: 'left' | 'right'
  className?: string
  children: ReactNode
}) {
  return (
    <div
      role="dialog"
      aria-label={label}
      className={clsx(
        'absolute top-full z-40 mt-1 rounded-lg border border-amber-300/20 bg-[#121212] p-2.5 shadow-2xl',
        align === 'right' ? 'right-0' : 'left-0',
        className,
      )}
    >
      {children}
    </div>
  )
}

/** A modal over the Studio. Escape and the backdrop close it; focus is not trapped. */
export function StudioDialog({
  label,
  onClose,
  width = 'max-w-3xl',
  children,
}: {
  label: string
  onClose: () => void
  width?: string
  children: ReactNode
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className={clsx(
          'flex max-h-[92vh] w-full flex-col overflow-hidden rounded-xl border border-amber-300/20 bg-[#0d0d0d] shadow-2xl',
          width,
        )}
      >
        <div className="flex shrink-0 items-center gap-2 border-b border-amber-300/10 px-4 py-3">
          <h2 className="text-[14px] font-semibold text-stone-100">{label}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={`Close ${label}`}
            className="ml-auto rounded p-1 text-stone-500 hover:bg-white/5 hover:text-stone-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </section>
    </div>
  )
}
